import { MessageSquare } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import socketService from '@/shared/socket/socket.service'

import { useComments } from '../../hooks/useComments'

import CommentCard from './CommentCard'
import CommentInput from './CommentInput'

export default function LeadCommentsModal({
  open,
  onOpenChange,
  lead,
  conversationWith,
}) {
  const {
    comments,
    notifications,
    readCommentExpiry,
    isLoading,
    isSubmitting,
    fetchComments,
    createComment,
    updateComment,
    deleteComment,
    addComment,
    replaceComment,
    removeComment,
    clearComments,
    markNotificationsAsRead,
    setReadCommentExpiry,
  } = useComments()
  const [message, setMessage] = useState('')
  const [editingComment, setEditingComment] = useState(null)
  const scrollContainerRef = useRef(null)
  const bottomRef = useRef(null)

  useEffect(() => {
    if (!open) return

    bottomRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'end',
    })
  }, [comments, open])

  useEffect(() => {
    if (!open || !lead?._id) return

    fetchComments(lead._id)

    const unreadNotificationIds = notifications
      .filter(
        (notification) =>
          notification.leadId === lead._id && !notification.isRead,
      )
      .map((notification) => notification.id)

    if (!unreadNotificationIds.length) return

    const markAsRead = async () => {
      try {
        await markNotificationsAsRead(unreadNotificationIds)

        setReadCommentExpiry(unreadNotificationIds)
      } catch (error) {
        console.error('Failed to mark comment notifications as read:', error)
      }
    }

    markAsRead()
  }, [
    open,
    lead?._id,
    fetchComments,
    notifications,
    markNotificationsAsRead,
    setReadCommentExpiry,
  ])

  useEffect(() => {
    if (!open || !lead?._id) return

    const timers = []

    notifications.forEach((notification) => {
      if (notification.leadId !== lead._id || !notification.isRead) {
        return
      }

      const expiresAt = readCommentExpiry[notification.id]

      if (!expiresAt) return

      const remainingTime = expiresAt - Date.now()

      if (remainingTime <= 0) {
        removeComment(notification.commentId)
        return
      }

      const timer = setTimeout(() => {
        removeComment(notification.commentId)
      }, remainingTime)

      timers.push(timer)
    })

    return () => {
      timers.forEach(clearTimeout)
    }
  }, [open, lead?._id, notifications, readCommentExpiry, removeComment])

  useEffect(() => {
    if (!open || !lead?._id) return

    const socket = socketService.connect()

    if (!socket) return undefined

    const leadId = lead._id

    const joinLead = () => {
      socket.emit('lead:join', { leadId })
    }

    // Wait until handshake succeeds — emit before connect is dropped on auth failure
    if (socket.connected) {
      joinLead()
    } else {
      socket.once('connect', joinLead)
    }

    const handleNewComment = (comment) => {
      if (comment.leadId !== leadId) return
      addComment(comment)
    }

    const handleUpdatedComment = (comment) => {
      if (comment.leadId !== leadId) return
      replaceComment(comment)
    }

    const handleDeletedComment = ({ id }) => {
      removeComment(id)
    }

    socket.on('comment:new', handleNewComment)
    socket.on('comment:updated', handleUpdatedComment)
    socket.on('comment:deleted', handleDeletedComment)

    return () => {
      socket.off('connect', joinLead)
      socket.off('comment:new', handleNewComment)
      socket.off('comment:updated', handleUpdatedComment)
      socket.off('comment:deleted', handleDeletedComment)

      if (socket.connected) {
        socket.emit('lead:leave', { leadId })
      }

      setMessage('')
      setEditingComment(null)
      clearComments()
    }
  }, [
    open,
    lead?._id,
    clearComments,
    addComment,
    replaceComment,
    removeComment,
  ])

  const handleSubmit = async () => {
    const value = message.trim()

    if (!value || isSubmitting) return

    try {
      if (editingComment) {
        await updateComment(editingComment.id, {
          message: value,
        })

        setEditingComment(null)
      } else {
        const result = await createComment({
          leadId: lead._id,
          message: value,
        })
        if (!result) return
      }

      setMessage('')
    } catch (error) {
      console.error(error)
    }
  }

  const handleEdit = (comment) => {
    setEditingComment(comment)
    setMessage(comment.message)
  }

  const handleDelete = async (comment) => {
    try {
      await deleteComment(comment.id)
    } catch (error) {
      console.error(error)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[min(620px,85dvh)] w-[calc(100vw-1.5rem)] sm:w-[460px] max-w-[calc(100vw-1.5rem)] sm:max-w-[460px] flex-col overflow-hidden rounded-2xl sm:rounded-[28px] p-0">
        <DialogHeader className="border-b px-4 sm:px-8 py-4 sm:py-5">
          <DialogTitle className="flex items-center gap-3 text-lg sm:text-[22px] font-semibold">
            <MessageSquare className="size-5" />
            <div>
              <p>Comments</p>
              {conversationWith && (
                <p className="text-xs sm:text-sm font-medium text-muted-foreground">
                  {conversationWith}
                </p>
              )}
            </div>
          </DialogTitle>
        </DialogHeader>
        <div
          ref={scrollContainerRef}
          className="flex-1 overflow-y-auto px-4 sm:px-7 py-3 sm:py-5"
        >
          {isLoading ? (
            <p className="py-20 text-center text-sm text-muted-foreground">
              Loading comments...
            </p>
          ) : comments.length ? (
            <div className="space-y-5">
              {comments.map((comment) => (
                <CommentCard
                  key={comment.id}
                  comment={comment}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                />
              ))}

              <div ref={bottomRef} />
            </div>
          ) : (
            <p className="py-20 text-center text-sm text-muted-foreground">
              No comments yet.
            </p>
          )}
        </div>

        <CommentInput
          value={message}
          onChange={setMessage}
          onSubmit={handleSubmit}
          loading={isSubmitting}
        />
      </DialogContent>
    </Dialog>
  )
}
